<template>
  <div data-test="orderDebts" class="my-6">
    <AppTableHeader>
      <template #left></template>
      <template #right>
        <div
          v-if="
            $allowedMutation(
              person.allowedMutations,
              'personDebtBalanceCreateOrUpdate'
            )
          "
          class="fipa--header-btn"
          @click="addItem"
        >
          <div class="ml-auto d-flex">
            <v-btn
              dark
              icon
              small
              class="edit add-btn"
              data-test="personDebtBalanceCreate"
            >
              <v-icon color="white" small>$iconPlus</v-icon>
            </v-btn>
            {{ $t("common:button.add", "Add", "Add button") }}
          </div>
        </div>
      </template>
    </AppTableHeader>

    <v-data-table
      :headers="headerFields"
      :items="person.personDebtBalances"
      :loading="refetchUnderway"
      :items-per-page="-1"
      :hide-default-header="$vuetify.breakpoint.smAndDown"
      class="entsys--table"
      hide-default-footer
    >
      <template #no-data>
        <div class="text-center">
          {{
            $t(
              "common:NoResults.messsage",
              "No data available",
              "No results message"
            )
          }}
        </div>
      </template>
      <template #item="{ item, expand, isExpanded }">
        <tr>
          <template v-if="$vuetify.breakpoint.mdAndUp">
            <td>
              {{ $dayjs.date(item.updatedAt) }}
              <div class="muted--text">
                {{ $dayjs(item.updatedAt).format("HH:mm:ss") }}
              </div>
            </td>
            <td>
              <AppShowOrganisationName
                v-if="item.organisation"
                :organisation="item.organisation"
              />
              <div>
                <i>
                  <template v-if="item.dataSourceType == 'MANUAL'">
                    {{
                      $t(
                        "people:ShoppingBasketPage.debtsTab.dataSourceType.MANUAL.label",
                        "Debt is handled and recorded manually by administrator of organisation"
                      )
                    }}
                  </template>
                  <template
                    v-else-if="item.dataSourceType == 'AUTOMATIC_INTERNAL'"
                  >
                    {{
                      $t(
                        "people:ShoppingBasketPage.debtsTab.dataSourceType.AUTOMATIC_INTERNAL.label",
                        "Debt is automatically calculated based on shopping basket"
                      )
                    }}
                  </template>
                </i>
              </div>
            </td>
            <td>
              <div v-if="item.internalNote" class="mb-2">
                <v-icon>$iconCommentDots</v-icon>
              </div>
              <div v-if="item.noteToPerson" class="mb-2">
                {{ item.noteToPerson }}
              </div>
              <div class="mb-2">
                <template v-if="item.dataSourceType == 'MANUAL'">
                  {{
                    $t(
                      "people:ShoppingBasketPage.debtsTab.dataSourceType.MANUAL.description",
                      "If you have questions on how to clear your debt balance or debt is recorded as a mistake, contact organisation or help desk."
                    )
                  }}
                </template>
                <template
                  v-else-if="item.dataSourceType == 'AUTOMATIC_INTERNAL'"
                >
                  {{
                    $t(
                      "people:ShoppingBasketPage.debtsTab.dataSourceType.AUTOMATIC_INTERNAL.description",
                      "Not paid items in shopping basket. Payment requested immediately via your shopping basket."
                    )
                  }}
                </template>
              </div>
            </td>
            <td>
              <div class="accent--text currency-display">
                {{ masterCurrency(item.balanceCents) }}
              </div>
            </td>
          </template>
          <td v-else>
            <div class="mb-2">
              {{ $dayjs.date(item.updatedAt) }}
              <div class="muted--text">
                {{ $dayjs(item.updatedAt).format("HH:mm:ss") }}
              </div>
            </div>

            <div class="mb-4">
              <AppShowOrganisationName
                v-if="item.organisation"
                :organisation="item.organisation"
              />
              <div>
                <i>
                  <template v-if="item.dataSourceType == 'MANUAL'">
                    {{
                      $t(
                        "people:ShoppingBasketPage.debtsTab.dataSourceType.MANUAL.label",
                        "Debt is handled and recorded manually by administrator of organisation"
                      )
                    }}
                  </template>
                  <template
                    v-else-if="item.dataSourceType == 'AUTOMATIC_INTERNAL'"
                  >
                    {{
                      $t(
                        "people:ShoppingBasketPage.debtsTab.dataSourceType.AUTOMATIC_INTERNAL.label",
                        "Debt is automatically calculated based on shopping basket"
                      )
                    }}
                  </template>
                </i>
              </div>
            </div>

            <div class="mb-2">
              <div v-if="item.internalNote" class="mb-4">
                <v-icon>$iconCommentDots</v-icon>
              </div>
              <div v-if="item.noteToPerson" class="mb-4">
                {{ item.noteToPerson }}
              </div>

              <div>
                <template v-if="item.dataSourceType == 'MANUAL'">
                  {{
                    $t(
                      "people:ShoppingBasketPage.debtsTab.dataSourceType.MANUAL.description",
                      "If you have questions on how to clear your debt balance or debt is recorded as a mistake, contact organisation or help desk."
                    )
                  }}
                </template>
                <template
                  v-else-if="item.dataSourceType == 'AUTOMATIC_INTERNAL'"
                >
                  {{
                    $t(
                      "people:ShoppingBasketPage.debtsTab.dataSourceType.AUTOMATIC_INTERNAL.description",
                      "Not paid items in shopping basket. Payment requested immediately via your shopping basket."
                    )
                  }}
                </template>
              </div>
            </div>

            <div class="mb-2">
              <div class="accent--text currency-display">
                {{ masterCurrency(item.balanceCents) }}
              </div>
            </div>
          </td>
          <td style="min-width: 40px;">
            <AppButtonPositionSlot>
              <v-btn
                v-if="
                  $allowedMutation(
                    item.allowedMutations,
                    'personDebtBalanceCreateOrUpdate'
                  )
                "
                icon
                data-test="personDebtBalanceUpdate"
                class="edit-green ma-2"
                small
                :aria-label="$t('common:editButton.alt')"
                @click="editItem(item)"
              >
                <v-icon color="white" small>$edit</v-icon>
              </v-btn>
              <v-btn
                v-if="
                  $allowedMutation(
                    item.allowedMutations,
                    'personDebtBalanceDelete'
                  )
                "
                class="remove ma-2"
                data-test="personDebtBalanceDelete"
                :aria-label="$t('common:deleteButton.alt')"
                icon
                small
                @click="deletePersonDebtBalance(item, $event)"
              >
                <v-icon color="white" small>$iconTrash</v-icon>
                <v-progress-circular
                  v-show="false"
                  color="white"
                  size="21"
                  indeterminate
                />
              </v-btn>

              <v-btn
                v-if="item.internalNote"
                icon
                small
                class="ma-2"
                data-test="expand"
                :aria-label="$t('common:expandButton.alt')"
                @click="expand(!isExpanded)"
              >
                <v-icon color="primary">
                  {{
                    !isExpanded
                      ? "$iconCircleCaretDown"
                      : "$iconCircleCaretUp"
                  }}
                </v-icon>
              </v-btn>
            </AppButtonPositionSlot>
          </td>
        </tr>
      </template>

      <template #expanded-item="{item}">
        <td class="strong" colspan="99">
          <div class="mb-2">
            <b>{{
              $t(
                "people:common.officials.internalNote.heading",
                "Internal note"
              )
            }}</b>
          </div>
          {{ item.internalNote }}
        </td>
      </template>
    </v-data-table>

    <OrderDebtForm ref="form" @refetch="$emit('refetch')" />
  </div>
</template>
<script>
import { defineComponent, ref } from "vue";
import gql from "fraql";

import { useCurrency } from "@composables/masterdata/config";
import AppTableHeader from "@elements/AppTableHeader.vue";
import AppShowOrganisationName from "@AppShows/organisation/AppShowOrganisationName.vue";
import OrderDebtForm from "./OrderDebtForm.vue";
import AppButtonPositionSlot from "@elements/AppButtonPositionSlot.vue";

import { useOrderDebtHelper } from "./order_debt_helper";

export default defineComponent({
  name: "OrderDebts",
  components: {
    AppTableHeader,
    AppButtonPositionSlot,
    AppShowOrganisationName,
    OrderDebtForm
  },
  props: {
    person: {
      type: Object,
      required: true
    }
  },
  setup() {
    const { deletePersonDebtBalance } = useOrderDebtHelper();

    const refetchUnderway = ref(false); // value is set in parent component
    return {
      ...useCurrency(),
      deletePersonDebtBalance,
      refetchUnderway
    };
  },
  computed: {
    headerFields() {
      return [
        {
          text: this.$t(
            "people:ShoppingBasketPage.debtsTab.updatedAt.label",
            "Debt balance updated at"
          ),
          sortable: false
        },
        {
          text: this.$t(
            "people:ShoppingBasketPage.debtsTab.debtBalanceToOrganisation.label",
            "Debts balance to organisation"
          ),
          sortable: false
        },
        {
          text: this.$t(
            "people:ShoppingBasketPage.debtsTab.detailsAndInstructions.label",
            "Details and instructions"
          ),
          sortable: false
        },
        {
          text: this.$t(
            "people:ShoppingBasketPage.debtsTab.totalDebtAmount.label",
            "Total debt amount"
          ),
          sortable: false
        },
        {
          text: "",
          sortable: false
        }
      ];
    }
  },
  methods: {
    addItem() {
      this.$refs.form.openForm({ personId: this.person.id });
    },
    editItem(item) {
      this.$refs.form.openForm(item);
    }
  },
  fragments: {
    personDebtBalanceFields: gql`
      fragment _ on PersonDebtBalance {
        id
        personId
        organisationId
        organisation {
          id
          ${AppShowOrganisationName.fragments.organisation}
        }
        dataSourceType
        updatedAt
        noteToPerson
        balanceCents
        internalNote
        allowedMutations
        ${OrderDebtForm.fragments.personDebtBalanceFields}
      }
    `
  }
});
</script>
